import React, { useEffect, useRef, useState } from 'react';
import { HiChevronDown, HiOutlineClock, HiPlus } from 'react-icons/hi2';

const relativeTime = iso => {
  const then = Date.parse(iso || '');
  if (!Number.isFinite(then)) return '';
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return hours < 24 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`;
};

export default function SessionList({ sessions = [], sessionId, onSelect, onNew, children }) {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const trigger = useRef(null);
  const menu = useRef(null);
  const close = () => { setOpen(false); trigger.current?.focus(); };

  useEffect(() => {
    if (!open) return undefined;
    menu.current?.querySelector('[role="menuitem"]')?.focus();
    const outside = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);
    document.addEventListener('focusin', outside);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
      document.removeEventListener('focusin', outside);
    };
  }, [open]);

  const onKeyDown = event => {
    if (!open) {
      if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const items = Array.from(menu.current.querySelectorAll('[role="menuitem"]'));
      const current = items.indexOf(document.activeElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
        : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
      items[next]?.focus();
    }
  };

  return (
    <div className="chat-sessions" ref={root} onKeyDown={onKeyDown}>
      <button ref={trigger} type="button" className="chat-session-trigger" aria-label="Conversations"
        aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(value => !value)}>
        {children || 'Conversations'}
        <HiChevronDown className="chat-session-chevron" aria-hidden="true" />
      </button>
      {open && <div ref={menu} role="menu" aria-label="Conversations" className="chat-session-menu">
        <button type="button" role="menuitem" className="chat-session-new" onClick={() => { onNew(); close(); }}>
          <HiPlus aria-hidden="true" /> New chat
        </button>
        <div className="chat-session-heading">Recent</div>
        <div className="chat-sessions-list">
          {!sessions.length && <p>No earlier chats</p>}
          {sessions.map(session => <button type="button" role="menuitem" key={session.sessionId}
            aria-current={session.sessionId === sessionId ? 'true' : undefined}
            onClick={() => { onSelect(session.sessionId); close(); }}>
            <HiOutlineClock className="chat-session-clock" aria-hidden="true" />
            <span className="chat-session-details">
              <span className="chat-session-title">{session.title || 'Untitled chat'}</span>
              <span className="chat-session-time">{relativeTime(session.updatedAt)}</span>
            </span>
          </button>)}
        </div>
      </div>}
    </div>
  );
}
